import { useState } from 'react';
import { api, ApiError } from '../api';
import { formatRelative } from '../format';
import type { LeadScore } from '../types';

/**
 * The AI lead rating out of 10. The number is always shown — colour is a
 * second signal, never the only one. The reason shows on hover.
 */
export function LeadScoreBadge({ lead, compact = false }: { lead: LeadScore | null; compact?: boolean }) {
  if (!lead) {
    return (
      <span className="muted small" title="Not rated yet — the AI rates leads in the background once an AI key is set up in Settings.">
        —
      </span>
    );
  }
  const band = lead.score >= 7 ? 'high' : lead.score >= 4 ? 'mid' : 'low';
  return (
    <span className={`lead-score lead-score-${band}`} title={leadScoreSummary(lead)}>
      <span className="dot" aria-hidden="true" />
      <strong>{formatScore(lead.score)}</strong>
      <span className="muted small">/10</span>
      <span className="ai-spark small" aria-label="AI rating">
        ✦
      </span>
      {!compact && lead.temperature ? (
        <span className={`temp temp-${lead.temperature}`}>{lead.temperature}</span>
      ) : null}
    </span>
  );
}

export function formatScore(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

/** The hover text: why the lead has this rating. */
export function leadScoreSummary(lead: LeadScore): string {
  const facts = [lead.temperature, lead.confidence ? `${lead.confidence} confidence` : null].filter(Boolean).join(', ');
  return [
    `AI lead rating ${formatScore(lead.score)}/10${facts ? ` (${facts})` : ''}`,
    lead.reason ?? '',
    lead.ratedAt ? `Rated ${formatRelative(lead.ratedAt)}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/** The rating in the contact journey: the AI's verdict and reason, re-rate, and the form answers. */
export function LeadRatingPanel({ contactId, lead }: { contactId: string; lead: LeadScore | null }) {
  const [current, setCurrent] = useState<LeadScore | null>(lead);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function rerate() {
    setBusy(true);
    setError(null);
    try {
      const { rating } = await api.rateContact(contactId);
      if (rating) {
        setCurrent((previous) => ({
          score: rating.score,
          source: 'ai',
          reason: rating.reason,
          temperature: rating.temperature,
          confidence: rating.confidence,
          ratedAt: rating.ratedAt,
          ratedFrom: 'batch',
          form: previous?.form ?? null,
        }));
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const form = current?.form ?? null;
  return (
    <div className={`lead-rating${busy ? ' reloading' : ''}`}>
      <div className="spread">
        <div className="cell-sub">Lead rating</div>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={rerate}>
          {busy ? 'Rating…' : current ? 'Re-rate' : 'Rate with AI'}
        </button>
      </div>
      <div className="row">
        <LeadScoreBadge lead={current} />
        {current?.confidence ? (
          <span className="muted small">{current.confidence} confidence</span>
        ) : null}
        {current?.ratedAt ? <span className="muted small">· rated {formatRelative(current.ratedAt)}</span> : null}
      </div>
      {current?.reason ? <p className="ai-text" style={{ marginTop: 4 }}>{current.reason}</p> : null}
      {!current ? <p className="small muted" style={{ margin: '4px 0 0' }}>Not rated yet.</p> : null}
      {error ? <div className="inline-result bad">{error}</div> : null}
      {form ? (
        <details className="lead-breakdown">
          <summary className="small muted">
            Form answers · {form.answered} of {form.questions} answered
          </summary>
          <table>
            <tbody>
              {form.detail.map((item) => (
                <tr key={item.key}>
                  <td>{item.label}</td>
                  <td>
                    {item.status === 'scored' ? (
                      <>
                        {item.reading}
                        {item.answer && item.answer !== item.reading ? <div className="cell-sub">“{item.answer}”</div> : null}
                      </>
                    ) : item.status === 'unreadable' ? (
                      <span className="muted">“{item.answer}” — could not be read</span>
                    ) : (
                      <span className="muted">Not answered</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}
